import {
  Alert,
  Button,
  Group,
  Loader,
  Modal,
  ScrollArea,
  Stack,
  Text,
} from '@mantine/core';
import { useTranslation } from '@refinedev/core';
import type { UseQueryResult } from '@tanstack/react-query';
import type { Comment } from '@billing/validation/comment';
import { useState } from 'react';
import { useDeleteClaimComment } from 'api/resource/claimComment';
import { ClaimCommentMessage } from './ClaimCommentMessage';

export const ClaimCommentHistory = ({
  commentsQuery,
  timezone,
  claimID,
  canDeleteComment = false,
  compact = false,
}: {
  commentsQuery: UseQueryResult<Comment[], Error>;
  timezone: string;
  claimID?: string;
  canDeleteComment?: boolean;
  compact?: boolean;
}) => {
  const { translate } = useTranslation();
  const [selectedCommentID, setSelectedCommentID] = useState<string | null>(
    null
  );
  const deleteComment = useDeleteClaimComment(claimID);
  const closeDelete = () => {
    if (deleteComment.isPending) return;
    setSelectedCommentID(null);
    deleteComment.reset();
  };
  const confirmDelete = () => {
    if (!selectedCommentID || deleteComment.isPending) return;
    deleteComment.mutate(selectedCommentID, {
      onSuccess: () => setSelectedCommentID(null),
    });
  };
  const { data: comments, isLoading, isError, refetch } = commentsQuery;

  if (isLoading) {
    return (
      <Group role="status">
        <Loader size="sm" />
        <Text size="sm">
          {translate('pages:claims.comments.loading', 'Loading comments…')}
        </Text>
      </Group>
    );
  }

  if (isError) {
    return (
      <Alert color="red">
        <Group justify="space-between">
          <Text size="sm">
            {translate(
              'pages:claims.comments.loadError',
              'Comments could not be loaded.'
            )}
          </Text>
          <Button variant="subtle" size="xs" onClick={() => refetch()}>
            {translate('pages:claims.comments.retry', 'Retry')}
          </Button>
        </Group>
      </Alert>
    );
  }

  if (comments?.length === 0) {
    if (compact) return null;

    return (
      <Text size="sm" c="dimmed">
        {translate('pages:claims.comments.empty', 'No comments yet.')}
      </Text>
    );
  }

  return (
    <>
      <ScrollArea.Autosize mah={400} type="auto" pr="sm">
        <Stack
          role="list"
          gap={compact ? 8 : 'sm'}
          aria-label={translate(
            'pages:claims.comments.timeline',
            'Claim comment timeline'
          )}
        >
          {comments.map((comment) => (
            <ClaimCommentMessage
              key={comment.id}
              comment={comment}
              timezone={timezone}
              onDelete={
                canDeleteComment && claimID
                  ? () => setSelectedCommentID(comment.id)
                  : undefined
              }
            />
          ))}
        </Stack>
      </ScrollArea.Autosize>
      <Modal
        opened={selectedCommentID !== null}
        onClose={closeDelete}
        title={translate(
          'pages:claims.comments.deleteTitle',
          'Delete internal comment'
        )}
        centered
        closeOnEscape={!deleteComment.isPending}
        closeOnClickOutside={!deleteComment.isPending}
        withCloseButton={!deleteComment.isPending}
      >
        <Stack>
          <Text size="sm">
            {translate(
              'pages:claims.comments.deleteConfirmation',
              'Are you sure you want to delete this internal comment? This cannot be undone.'
            )}
          </Text>
          {deleteComment.isError && (
            <Alert color="gray" c="red" role="alert">
              {translate(
                'pages:claims.comments.deleteError',
                'The comment could not be deleted. Please try again.'
              )}
            </Alert>
          )}
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={closeDelete}
              disabled={deleteComment.isPending}
            >
              {translate('pages:claims.comments.cancelDelete', 'Cancel')}
            </Button>
            <Button
              onClick={confirmDelete}
              loading={deleteComment.isPending}
              c="red"
              color="gray"
            >
              {translate('pages:claims.comments.delete', 'Delete comment')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};
